import { get, merge } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS, HTTP_RESPONSE_STATUS } from 'constants/common';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';

// get store
const store = getStore();

// This bounded-search rejection is user-actionable and safe to show. Other
// server descriptions stay hidden unless the response carries a known reason.
const TOO_MANY_MEMBER_MATCHES_CODE = 'general';
const TOO_MANY_MEMBER_MATCHES_MESSAGE = 'Too many matching members. Please narrow your search.';

/*************************************************************/

/**
 * @type {class}
 * @desc TableSearchWidget defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { TableSearchWidgetService } from 'services/tableSearchWidget';
 *
 * // create service instance and send/cancel request
 * const tableSearchWidgetService = new TableSearchWidgetService();
 * const result = await tableSearchWidgetService.fetchData( '<user-id>' ); // fetch data
 * tableSearchWidgetService.cancel(); // cancel an active request
 */
export class TableSearchWidgetService extends HttpService {

    // fetch new table items based on filters
    async fetchItems( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( {}, data, {
                    context: {
                        callId,
                        callType,
                    },
                } ),
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                items: get( response, 'body.component.data.state.items', [] ),
                liveFallback: true === get( response, 'body.component.data.state.liveFallback', false ),
                liveMemberLookup: true === get( response, 'body.component.data.state.liveMemberLookup', false ),
                paginationCapable: get( response, 'body.component.data.state.paginationCapable', true ),
                totalRecordCount: get( response, 'body.component.data.state.totalRecordCount', null ),
                sourceAsOf: get( response, 'body.component.data.sourceAsOf', get( response, 'body.component.data.state.sourceAsOf', null ) ),
                // a specific, client-safe failure message and reference code, present only when the app knows the reason
                failureReason: get( response, 'body.component.data.reason', null ),
                failureMessage: this.isTooManyMemberMatchesFailure( response )
                    ? TOO_MANY_MEMBER_MATCHES_MESSAGE
                    : null
            };
        } catch( error ) {
            if( HTTP_RESPONSE_STATUS.NONE === get( error, 'data.status', 0 ) ) {
                return {
                    status: SERVICE_RESPONSE_STATUS.CANCELLED,
                    data: null,
                };
            } else {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE,
                    data: null,
                };
            }
        }
    }

    isTooManyMemberMatchesFailure( response ) {
        return get( response, 'body.component.data.code', null ) === TOO_MANY_MEMBER_MATCHES_CODE
            && get( response, 'body.component.data.description', null ) === TOO_MANY_MEMBER_MATCHES_MESSAGE;
    }

    // fetch new table items based on filters
    async refreshItem( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge(
                    {
                        context: {
                            callId,
                            callType,
                            callerType
                        }
                    },
                    data
                ),
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: get( response, 'body.component.data.state.items', [] ),
            };
            
        } catch( error ) {
            if( HTTP_RESPONSE_STATUS.NONE === get( error, 'data.status', 0 ) ) {
                return {
                    status: SERVICE_RESPONSE_STATUS.CANCELLED,
                    data: null,
                };
            } else {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE,
                    data: null,
                };
            }
        }
    }

    async fetchFilterSearchOptions( { url, data  } ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                options: get( response, 'body.component.data.options', [] ),
            };
            
        } catch( error ) {
            if( HTTP_RESPONSE_STATUS.NONE === get( error, 'data.status', 0 ) ) {
                return {
                    status: SERVICE_RESPONSE_STATUS.CANCELLED,
                    data: null,
                };
            } else {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE,
                    data: null,
                };
            }
        }
    }
}
